import { Box, Checkbox, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  BuildChecklistEntity,
  ChecklistItemEntity,
  PreferenceCardEntity,
} from '@peritasai/repository';
import React from 'react';

const CartListContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const CartGroupContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const CartGroupHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const CartItemContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'disabled',
})<{ isSelected?: boolean; disabled?: boolean }>(
  ({ theme, isSelected, disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${
      isSelected ? theme.palette.primary.main : theme.palette.divider
    }`,
    backgroundColor: isSelected
      ? theme.palette.primary.light + '10'
      : theme.palette.background.paper,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: disabled
        ? theme.palette.divider
        : theme.palette.primary.main,
      backgroundColor: disabled
        ? theme.palette.action.disabledBackground
        : theme.palette.action.hover,
    },
    opacity: disabled ? 0.5 : 1,
  })
);

const CartItemInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: 4,
});

const CartItemLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMedium,
}));

const CartItemDetails = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 1,
}));

interface CartSelectionStepProps {
  preferenceCard: PreferenceCardEntity;
  buildChecklist: BuildChecklistEntity;
  onCartSelect: (checklistItem: ChecklistItemEntity) => Promise<void>;
  isLoading?: boolean;
}

export const CartSelectionStep: React.FC<CartSelectionStepProps> = ({
  preferenceCard,
  buildChecklist,
  onCartSelect,
  isLoading = false,
}) => {
  const availableCarts = preferenceCard.trackedAssets.caseCarts;
  const checklistGroupByCatalogId = React.useMemo(() => {
    const map = new Map<
      string,
      ReturnType<BuildChecklistEntity['getAvailableCarts']>[number] | undefined
    >();
    buildChecklist.getAvailableCarts().forEach((group) => {
      map.set(group.catalogItemId, group);
    });
    return map;
  }, [buildChecklist]);

  const handleCartItemClick = async (checklistItem?: ChecklistItemEntity) => {
    if (isLoading || !checklistItem) return;
    // When user checks an item, it will be allocated and get assetId
    await onCartSelect(checklistItem);
  };

  if (availableCarts.length === 0) {
    return (
      <Box sx={{ padding: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No carts available for selection
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {isLoading && (
        <LoadingOverlay>
          <CircularProgress size={40} />
        </LoadingOverlay>
      )}
      <CartListContainer>
        {availableCarts.map((cartAsset) => {
          const checklistGroup = checklistGroupByCatalogId.get(
            cartAsset.catalogItemId
          );
          const checklistItems = checklistGroup?.items ?? [];
          const rowCount = Math.max(
            cartAsset.quantity || 1,
            checklistItems.length || 0,
            1
          );

          return (
            <CartGroupContainer key={cartAsset.catalogItemId}>
              <CartGroupHeader>
                <Typography variant="h6" fontWeight={600}>
                  {cartAsset.name}
                </Typography>
                {cartAsset.productNumber && (
                  <Typography variant="caption" color="text.secondary">
                    Product: {cartAsset.productNumber}
                    {cartAsset.manufacturer && ` • ${cartAsset.manufacturer}`}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Qty Required: {cartAsset.quantity}, , available:{' '}
                  {cartAsset.availableCount}
                </Typography>
              </CartGroupHeader>
              {Array.from({ length: rowCount }).map((_, index) => {
                const checklistItem = checklistItems[index];
                const isSelected = checklistItem?.allocated ?? false;
                const isDisabled = !checklistItem;

                return (
                  <CartItemContainer
                    key={`${cartAsset.catalogItemId}-${index}`}
                    isSelected={isSelected}
                    disabled={isDisabled}
                    onClick={() => handleCartItemClick(checklistItem)}
                  >
                    <CartItemInfo>
                      <CartItemLabel>
                        Cart{' '}
                        {checklistItem?.assetId ||
                          `${cartAsset.name} #${index + 1}`}
                      </CartItemLabel>
                      {checklistItem?.notes && (
                        <CartItemDetails>{checklistItem.notes}</CartItemDetails>
                      )}
                      {isDisabled && (
                        <CartItemDetails>
                          Checklist item not available yet
                        </CartItemDetails>
                      )}
                    </CartItemInfo>
                    <Checkbox
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => handleCartItemClick(checklistItem)}
                      color="primary"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </CartItemContainer>
                );
              })}
            </CartGroupContainer>
          );
        })}
      </CartListContainer>
    </Box>
  );
};
